import React from 'react';
import { Link, type To } from 'react-router-dom';
import { ChatPlusIn, MultiBubble, Search, Clock, Tools, Puzzle } from 'iconoir-react';
import { cn } from 'lib/utils';
import { sidebarNavItemLabelClass, sidebarNavItemLabelActiveClass } from './sidebar-nav-label';
import {
  sidebarNavIconClass,
  SIDEBAR_NAV_ICON_SIZE,
  SIDEBAR_NAV_ICON_STROKE,
} from './sidebar-nav-icon';
import {
  sidebarNavItemClassName,
  sidebarNavItemActiveClassName,
  sidebarNavItemIconOnlyClassName,
} from './sidebar-nav-item-styles';
import { SidebarNavTooltip } from './sidebar-nav-tooltip';
import { LibraryNavIcon } from './icons/library-nav-icon';
import { CreateAgentIcon } from './icons/create-agent-icon';

export type SidebarPrimaryNavIcon = 'new-chat' | 'group-chat' | 'library' | 'agents' | 'integrations' | 'tools' | 'search' | 'scheduled';

const primaryNavIcons = {
  'new-chat': ChatPlusIn,
  'group-chat': MultiBubble,
  library: LibraryNavIcon,
  agents: CreateAgentIcon,
  integrations: Puzzle,
  tools: Tools,
  search: Search,
  scheduled: Clock,
} as const;

interface SidebarPrimaryNavItemProps {
  label: string;
  icon: SidebarPrimaryNavIcon;
  to?: To;
  onClick?: () => void;
  isActive?: boolean;
  disabled?: boolean;
  showText?: boolean;
}

const itemClassName = sidebarNavItemClassName;

export function SidebarPrimaryNavItem({
  label,
  icon,
  to,
  onClick,
  isActive = false,
  disabled = false,
  showText = true,
}: SidebarPrimaryNavItemProps) {
  const Icon = primaryNavIcons[icon];

  const content = (
    <>
      <Icon
        width={SIDEBAR_NAV_ICON_SIZE}
        height={SIDEBAR_NAV_ICON_SIZE}
        strokeWidth={SIDEBAR_NAV_ICON_STROKE}
        className={sidebarNavIconClass}
      />
      {showText && (
        <span className={cn(sidebarNavItemLabelClass, isActive && sidebarNavItemLabelActiveClass, 'truncate')}>{label}</span>
      )}
    </>
  );

  if (to && !disabled) {
    return (
      <SidebarNavTooltip label={label} enabled={!showText}>
        <Link
          to={to}
          onClick={onClick}
          aria-current={isActive ? 'page' : undefined}
          aria-label={!showText ? label : undefined}
          className={cn(
            itemClassName,
            !showText && sidebarNavItemIconOnlyClassName,
            isActive && sidebarNavItemActiveClassName
          )}
        >
          {content}
        </Link>
      </SidebarNavTooltip>
    );
  }

  return (
    <SidebarNavTooltip label={label} enabled={!showText}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={!showText ? label : undefined}
        className={cn(
          itemClassName,
          !showText && sidebarNavItemIconOnlyClassName,
          isActive && sidebarNavItemActiveClassName
        )}
      >
        {content}
      </button>
    </SidebarNavTooltip>
  );
}
